import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Reorder, useDragControls } from "framer-motion";
import { MdDragIndicator, MdRemoveCircleOutline } from "react-icons/md";
import {
  cleanSymbol,
  shortName,
  formatPrice,
  formatDayChange,
  dayChangeColor,
  chartSymbolFor,
  ZERO,
} from "../watchlistFormat";
import ListSkeleton from "../../Skeleton/ListSkeleton";
import useLeagueAssetGate from "../useLeagueAssetGate";

function TealChip({ symbol }) {
  return (
    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primaryColor">
      <span className="truncate px-0.5 text-[9px] font-semibold leading-tight text-white">{symbol}</span>
    </div>
  );
}

function Metrics({ item, dim }) {
  const price = formatPrice(item?.details?.latestData?.c);
  const raw = item?.details?.percentageChange;
  const change = formatDayChange(raw);
  return (
    <div className={`shrink-0 text-right ${dim ? "opacity-40" : ""}`}>
      <div className="text-sm font-semibold text-black">{price ?? <span className="text-tertiary">—</span>}</div>
      <div className="text-xs font-semibold" style={{ color: change ? dayChangeColor(raw) : ZERO }}>
        {change ?? "—"}
      </div>
    </div>
  );
}

// A view-mode asset row (personal = teal chip, curated = real logo + descriptor).
//
// `gate` decides where the row opens — the main chart, this league's Trade
// screen, or nowhere with "Not eligible in this league". See useLeagueAssetGate.
function ViewRow({ item, isCurated, gate }) {
  const rowClass = "flex items-center gap-3 border-b border-gray-100 py-2.5";
  const href = gate ? gate.linkFor(item) : `/chartSearch/${chartSymbolFor(item)}`;

  const body = (
    <>
      {isCurated ? (
        <img src={item.logo || "/bitcoin.png"} alt="" className="h-9 w-9 shrink-0 rounded-full object-contain" />
      ) : (
        <TealChip symbol={cleanSymbol(item.symbol)} />
      )}
      <div className="min-w-0 flex-1">
        {isCurated ? (
          <>
            <div className="truncate font-montserrat text-[15px] font-semibold text-black">
              {item.watchlist_name || cleanSymbol(item.symbol)}
            </div>
            {item.description ? (
              <div className="truncate text-xs text-secondary">{item.description}</div>
            ) : null}
          </>
        ) : (
          <div className="truncate font-montserrat text-[15px] text-black" title={item?.details?.stock_name || ""}>
            {shortName(item?.details?.stock_name)}
          </div>
        )}
      </div>
      <Metrics item={item} />
    </>
  );

  // An ineligible row inside a league still renders — client: "Nothing is
  // visually filtered out of the watchlist itself." It just stops being a link,
  // and says why when tapped.
  if (!href) {
    return (
      <button type="button" onClick={() => gate.onOpen(item)} className={`w-full text-left ${rowClass}`}>
        {body}
      </button>
    );
  }

  return (
    <Link
      to={href}
      onClick={(e) => {
        if (gate && !gate.onOpen(item)) e.preventDefault();
        else if (!gate) localStorage.setItem("ticker", chartSymbolFor(item));
      }}
      className={rowClass}
    >
      {body}
    </Link>
  );
}

// An edit-mode asset row: drag handle + chip + name + dimmed metrics + remove.
function EditRow({ item, onRemove, onDragCommit }) {
  const controls = useDragControls();
  return (
    <Reorder.Item
      as="div"
      value={item}
      dragListener={false}
      dragControls={controls}
      onDragEnd={onDragCommit}
      className="flex items-center gap-2 border-b border-gray-100 bg-white py-2.5"
    >
      <span
        onPointerDown={(e) => controls.start(e)}
        className="shrink-0 cursor-grab touch-none text-tertiary"
        aria-label="Drag to reorder"
      >
        <MdDragIndicator size={20} />
      </span>
      <TealChip symbol={cleanSymbol(item.symbol)} />
      <div className="min-w-0 flex-1">
        <div className="truncate font-montserrat text-[15px] text-black">{shortName(item?.details?.stock_name)}</div>
      </div>
      <Metrics item={item} dim />
      <button
        type="button"
        aria-label="Remove"
        onClick={() => onRemove(item)}
        className="shrink-0 text-red-500 transition-colors hover:text-red-600"
      >
        <MdRemoveCircleOutline size={22} />
      </button>
    </Reorder.Item>
  );
}

// Screens 2/3/4 + edit mode.
export default function MobileListDetail({
  list,
  isCurated,
  isActive,
  count,
  items,
  itemsLoading,
  loadingMore,
  hasMore,
  editing,
  onLoadMore,
  onEnterEdit,
  onExitEdit,
  onOpenAdd,
  onUseInTrade,
  useInTradeBlocked = false,
  useInTradeBlockedMessage = "",
  onRemove,
  onReorderAssets,
  onRename,
  onDeleteList,
}) {
  // Where a row opens, and whether it may. Null-op on the main site; inside a
  // league it routes to the league's Trade screen and refuses assets the league
  // does not allow. Resolved once here rather than once per row.
  const gate = useLeagueAssetGate();

  const [draftName, setDraftName] = useState(list?.name || "");
  const [order, setOrder] = useState(items);
  const orderRef = useRef(items);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    setOrder(items);
    orderRef.current = items;
  }, [items]);
  useEffect(() => {
    if (editing) setDraftName(list?.name || "");
  }, [editing, list]);

  if (!list) {
    return (
      <div className="py-16 text-center text-sm text-secondary">List not found</div>
    );
  }

  const commitRename = () => {
    if (draftName.trim() && draftName.trim() !== list.name) onRename(draftName);
  };

  const handleReorder = (newOrder) => {
    setOrder(newOrder);
    orderRef.current = newOrder;
  };

  return (
    <div className="pb-8">
      {/* Title (the app header's back arrow returns to the picker) */}
      {editing ? (
        <input
          value={draftName}
          maxLength={24}
          autoFocus
          onChange={(e) => setDraftName(e.target.value)}
          onBlur={commitRename}
          onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
          className="w-full border-b border-dashed border-gray-300 bg-transparent font-montserrat text-title-subpage font-bold text-[#004d4d] focus:outline-none"
        />
      ) : (
        <h1 className="truncate font-montserrat text-title-subpage font-bold text-[#004d4d]">{list.name}</h1>
      )}

      {/* Subtitle */}
      <div className="mt-1 flex items-center gap-1.5 text-sm text-secondary">
        <span>{count} assets</span>
        <span>·</span>
        {isCurated ? (
          <span>Curated by QUSHi</span>
        ) : editing ? (
          <button type="button" onClick={onExitEdit} className="font-medium" style={{ color: "#004c50" }}>
            Done
          </button>
        ) : (
          <button type="button" onClick={onEnterEdit} className="font-medium" style={{ color: "#004c50" }}>
            Edit
          </button>
        )}
      </div>

      {/* Action buttons (hidden in edit mode) */}
      {!editing && (!isCurated || !isActive) && (
        <div className="mt-4 flex gap-3">
          {!isCurated && (
            <button
              type="button"
              onClick={onOpenAdd}
              className={`rounded-lg bg-primaryColor px-4 py-2 text-sm font-medium text-white ${
                !isActive ? "flex-1" : ""
              }`}
            >
              Add Assets
            </button>
          )}
          {!isActive && (
            <button
              type="button"
              id="watchlist-guide-use-in-trade"
              onClick={onUseInTrade}
              disabled={useInTradeBlocked}
              aria-disabled={useInTradeBlocked || undefined}
              className={`rounded-lg border px-4 py-2 text-sm font-medium ${
                useInTradeBlocked
                  ? "cursor-not-allowed border-gray-200 bg-gray-50 text-gray-400"
                  : "border-gray-300 bg-white text-primaryColor"
              } ${!isCurated ? "flex-1" : ""}`}
            >
              Use in Trade
            </button>
          )}
        </div>
      )}

      {/* Client Rev 2: "If a watchlist has zero eligible assets, disable Use in
          Trade and show: No assets in this list are eligible for this league."
          Shown on screen, not only as a tooltip — a phone has no hover. */}
      {!editing && useInTradeBlocked && (
        <p
          id="watchlist-use-in-trade-blocked"
          className="mt-2 text-sm text-secondary"
        >
          {useInTradeBlockedMessage}
        </p>
      )}

      {/* Asset list */}
      <div className="mt-4">
        {itemsLoading ? (
          <ListSkeleton
            rows={6}
            avatarSize={36}
            columns={2}
            columnWidths={[70, 56]}
            rowClassName="py-3.5"
          />
        ) : items.length === 0 ? (
          <div className="py-12 text-center text-sm text-secondary">No assets yet</div>
        ) : editing ? (
          <Reorder.Group axis="y" values={order} onReorder={handleReorder} as="div">
            {order.map((item) => (
              <EditRow
                key={item.id}
                item={item}
                onRemove={onRemove}
                onDragCommit={() => onReorderAssets(orderRef.current)}
              />
            ))}
          </Reorder.Group>
        ) : (
          items.map((item) => (
            <ViewRow key={item.id} item={item} isCurated={isCurated} gate={gate} />
          ))
        )}

        {/* Load more (view mode) */}
        {!editing && !itemsLoading && hasMore && items.length > 0 && (
          <div className="flex justify-center py-4">
            <button
              type="button"
              onClick={onLoadMore}
              disabled={loadingMore}
              className="text-sm font-medium text-primaryColor disabled:opacity-50"
            >
              {loadingMore ? "Loading" : "Load more"}
            </button>
          </div>
        )}
      </div>

      {/* Delete List (edit mode) */}
      {editing && (
        <button
          type="button"
          onClick={() => setConfirmDelete(true)}
          className="mt-6 flex items-center gap-2 text-sm font-semibold text-red-500"
        >
          <MdRemoveCircleOutline size={18} /> Delete List
        </button>
      )}

      {/* Delete confirm */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 px-4">
          <div className="w-full max-w-sm rounded-xl bg-white p-6 shadow-lg">
            <h3 className="mb-2 text-lg font-bold text-[#004d4d]">Delete list</h3>
            <p className="mb-5 text-sm text-secondary">Delete "{list.name}"? This cannot be undone.</p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirmDelete(false)}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-secondary"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  setConfirmDelete(false);
                  onDeleteList();
                }}
                className="rounded-lg bg-red-500 px-4 py-2 text-sm font-medium text-white"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
